"use client";

import { useState, useCallback } from "react";
import { UserPlus } from "lucide-react";
import { STATIC_ASSETS } from "@/lib/cdn";
import { useAuth } from "@/hooks/useAuth";
import { getAccessToken } from "@privy-io/react-auth";
import Image from "next/image";
import Link from "next/link";
import { useQueryClient } from "@tanstack/react-query";
import { useRecommendedUsersQuery, RECOMMENDED_USERS_QUERY_KEY } from "@/hooks/useRecommendedUsersQuery";
import { useT } from "@/hooks/useT";

export default function WhoToFollow() {
  const t = useT();
  const { isReady, isLoggedIn, userId: currentUserId, login } = useAuth();
  const { data: usersRaw = [], isLoading: loading } = useRecommendedUsersQuery();
  // The recommendation worker can include the requesting user in its
  // output (collaborative-filtering treats them as a candidate). Strip
  // them client-side so we don't waste a slot on "follow yourself".
  const users = currentUserId
    ? usersRaw.filter((u) => u.id !== currentUserId)
    : usersRaw;
  const queryClient = useQueryClient();
  const [followingState, setFollowingState] = useState<Record<string, boolean>>({});
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    try {
      const token = await getAccessToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    } catch {}
    return headers;
  }, []);

  const handleFollow = async (userId: string) => {
    if (!isLoggedIn) {
      login();
      return;
    }

    const isCurrentlyFollowing = followingState[userId];
    // Optimistic update — the button flips immediately so the click
    // feels instant. We rollback only if the request fails.
    setFollowingState((prev) => ({ ...prev, [userId]: !isCurrentlyFollowing }));
    setActionLoading((prev) => ({ ...prev, [userId]: true }));

    try {
      const headers = await getAuthHeaders();
      const response = await fetch("/api/user/follow", {
        method: "POST",
        headers,
        body: JSON.stringify({
          targetUserId: userId,
          action: isCurrentlyFollowing ? "unfollow" : "follow",
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Confirm with server's truth (in case of race with another tab)
        setFollowingState((prev) => ({ ...prev, [userId]: data.isFollowing }));
        // Patch the cached recommendations list directly. The backend
        // already excludes followed users from this endpoint, so the
        // followed user must disappear here too — otherwise the 5-min
        // staleTime keeps them visible, and on any remount our local
        // `followingState` resets to {} and the button reads "Follow"
        // again. The user concludes the follow didn't work even though
        // the row exists in the DB. Filtering locally on success keeps
        // visual state consistent across remounts and matches the next
        // server-fresh fetch (the user won't reappear).
        if (data.isFollowing) {
          queryClient.setQueryData(
            RECOMMENDED_USERS_QUERY_KEY,
            (old: any[] | undefined) =>
              Array.isArray(old) ? old.filter((u) => u.id !== userId) : old,
          );
        }
        // Stale-mark for the next natural mount/window-focus refetch.
        queryClient.invalidateQueries({
          queryKey: RECOMMENDED_USERS_QUERY_KEY,
          refetchType: 'none',
        });
      } else {
        // Server rejected — rollback the optimistic flip
        setFollowingState((prev) => ({ ...prev, [userId]: isCurrentlyFollowing }));
      }
    } catch (error) {
      setFollowingState((prev) => ({ ...prev, [userId]: isCurrentlyFollowing }));
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  if (!isReady || !isLoggedIn) {
    return null;
  }

  const cardClasses = "bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-5";

  if (loading) {
    return (
      <div className={cardClasses}>
        <h3 className="font-satoshi font-bold text-gray-900 dark:text-white text-sm mb-4 flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />{t("sidebar.suggested.title")}</h3>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex gap-3 items-center">
              <div className="w-10 h-10 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] flex-shrink-0" />
              <div className="flex-1">
                <div className="h-3.5 bg-gray-100 dark:bg-[#0f0f12] rounded w-24 mb-1.5" />
                <div className="h-2.5 bg-gray-100 dark:bg-[#0f0f12] rounded w-16" />
              </div>
              <div className="w-[68px] h-8 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className={cardClasses}>
        <h3 className="font-satoshi font-bold text-gray-900 dark:text-white text-sm mb-4 flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />{t("sidebar.suggested.title")}</h3>
        <p className="text-xs font-satoshi text-gray-500 dark:text-gray-400 text-center py-6">
          {t("sidebar.suggested.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className={cardClasses}>
      <h3 className="font-satoshi font-bold text-gray-900 dark:text-white text-sm mb-4 flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />{t("sidebar.suggested.title")}</h3>
      <div className="space-y-4">
        {users.slice(0, 3).map((user) => {
          const isFollowing = followingState[user.id];
          const isButtonLoading = actionLoading[user.id];
          const displayName = user.name || user.username;

          return (
            <div key={user.id} className="flex gap-3 items-center">
              <Link href={`/profile/${user.username}`} prefetch={false} className="flex-shrink-0">
                <div className="w-10 h-10 rounded-[5px] ring-1 ring-gray-200/80 dark:ring-white/[0.08] overflow-hidden">
                  <Image
                    src={user.image || STATIC_ASSETS.defaultAvatar}
                    alt={user.username}
                    width={40}
                    height={40}
                    sizes="40px"
                    className="w-full h-full rounded-[5px] object-cover"
                  />
                </div>
              </Link>
              <div className="flex-1 min-w-0">
                <Link
                  href={`/profile/${user.username}`}
                  prefetch={false}
                  className="block text-[13px] font-satoshi font-semibold text-gray-900 dark:text-white truncate hover:text-gray-600 dark:hover:text-gray-300 transition-colors leading-tight"
                >
                  {displayName}
                </Link>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[11px] font-satoshi text-gray-400 dark:text-gray-500">
                    @{user.username}
                  </span>
                  <span className="text-gray-300 dark:text-gray-600 text-[8px]">|</span>
                  <span className="text-[11px] font-satoshi text-gray-400 dark:text-gray-500">
                    {t(Number(user.followers) === 1 ? "sidebar.suggested.follower" : "sidebar.suggested.followers", { n: Number(user.followers) })}
                  </span>
                </div>
              </div>
              {isLoggedIn && currentUserId !== user.id && (
                <button
                  onClick={() => handleFollow(user.id)}
                  disabled={isButtonLoading}
                  className={`px-3.5 py-1.5 rounded-[5px] text-[11px] font-inter-tight font-bold transition-colors duration-200 flex-shrink-0 ${
                    isFollowing
                      ? "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-[#0f0f12] dark:text-gray-400 dark:hover:bg-[#151518]"
                      : "bg-gray-900 dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {/* No spinner mid-request — the optimistic state has
                      already flipped the label to "Following". Showing
                      a spinner over it would re-introduce the flicker
                      this commit is fixing. The button stays visually
                      `disabled:opacity-50` while the network call runs. */}
                  {isFollowing ? t("sidebar.suggested.following") : t("sidebar.suggested.follow")}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
